/**
 * App — root React component
 *
 * Responsibilities:
 * - Sets up `HashRouter` routes for the entire application
 * - Applies the persisted colour theme (light/dark) to `<html>`, `<body>`, and
 *   `#marketview` **before** first paint (`useLayoutEffect`) to prevent FOUC
 * - Keeps the DOM theme class in sync whenever the Redux theme state changes
 * - Kicks off the IPC layer (`getMachineId`, `registerIpcEvents`) on mount
 * - Starts / stops the `AlertEvaluationEngine` singleton with the component lifecycle
 *
 * Route structure:
 * ```
 * /                  → Login           (no navbar)
 * /ForgotPassword    → ForgotPassword  (no navbar)
 * /VerifyOtp         → VerifyOtp       (no navbar)
 * /ResetPassword     → ResetPassword   (no navbar)
 * /Chart             → Chart           (navbar, auth-guarded)
 * /Dashboard         → redirect → /Chart
 * /MarketWatch       → redirect → /Chart
 * /ChangePassword    → redirect → /Chart
 * ```
 */
import { useEffect, useLayoutEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { HashRouter as Router, Routes, Route, Navigate } from "react-router-dom";

import WithNav from "@components/navbar/WithNav";
import WithoutNav from "@components/navbar/WithoutNav";
import Login from "@components/login/Login";
import Chart from "@components/chart/Chart";
import { getMachineId, registerIpcEvents, checkForDesktopUpdate } from "@service/ipc-handler";
import { isDesktop, isMobile, platformType } from "@platform";
import { getColorTheme } from "@service/local-storage-handler";
import { themeReducer } from "@store/theme-slice";
import { alertEngine } from "../service/alert-engine";

import PrivateRoutes from "@components/auth-guard/PrivateRoute";
import AutoUpdateModal from "@components/auto-update/AutoUpdateModal";
import AppExitModal from "@components/app-exit/AppExitModal";
import Modal from "react-modal";
import ProgressBarModal from "@components/progress-bar/ProgressBarModal";
import MobileUpdateModal from "@components/auto-update/MobileUpdateModal";
import "../styles/platform-overrides.scss";
import ErrorBoundary from "@components/error-boundary/ErrorBoundary";
import "@components/error-boundary/ErrorBoundary.scss";
import { useKeyboardShortcuts } from "../hooks/useKeyboardShortcuts";
import { ToastContainer } from "@components/common/ToastContainer";

Modal.setAppElement("#marketview");

// Apply theme immediately before React renders to prevent flash
const savedTheme = (getColorTheme() as "light" | "dark") || "dark";
document.documentElement.classList.add(savedTheme);
document.body.classList.add(savedTheme);
// Add platform class to body for CSS targeting
document.body.classList.add(`platform-${platformType}`);
// Also apply to #marketview root element
const marketviewElement = document.getElementById("marketview");
if (marketviewElement) {
  marketviewElement.classList.add(savedTheme);
}

const App = () => {
  const dispacth = useDispatch();
  const themeFromStore = useSelector(({ theme }) => theme.theme);
  // Default to dark if theme is not set in Redux (fallback to localStorage)
  const currentTheme = themeFromStore || getColorTheme() || "dark";

  useEffect(() => {
    getMachineId();
    // Await listener registration before triggering the update check so the
    // confirm-download-update event is guaranteed to be handled.
    registerIpcEvents().then(() => {
      if (isDesktop) checkForDesktopUpdate();
    });
    // Start the price alert evaluation engine
    alertEngine.start();
    return () => alertEngine.stop();
  }, []);

  // Suppress native browser right-click context menu globally
  useEffect(() => {
    const suppress = (e: MouseEvent) => e.preventDefault();
    document.addEventListener("contextmenu", suppress);
    return () => document.removeEventListener("contextmenu", suppress);
  }, []);

  // ── Android hardware back button: single tap → ignore, double tap → exit ──
  // Uses window.Capacitor.Plugins.App directly (bypasses the build-time stub).
  // Registering ANY backButton listener in Capacitor suppresses the default
  // webView.goBack() call, preventing React Router from navigating to login.
  useEffect(() => {
    if (!isMobile) return;

    let lastBackTime = 0;
    let listenerHandle: { remove: () => void } | null = null;

    const handleBackButton = () => {
      const now = Date.now();
      if (now - lastBackTime <= 2000) {
        // Double tap within 2 s → exit the app
        (window as any).Capacitor?.Plugins?.App?.exitApp?.();
      } else {
        // First tap → record time, do nothing visible
        lastBackTime = now;
      }
    };

    const AppPlugin = (window as any).Capacitor?.Plugins?.App;
    if (AppPlugin?.addListener) {
      try {
        // Capacitor bridge returns handle synchronously (not a Promise)
        const handle = AppPlugin.addListener("backButton", handleBackButton);
        if (handle && typeof handle.remove === "function") {
          listenerHandle = handle;
        }
      } catch {
        /* Capacitor bridge not ready — ignore */
      }
    }

    return () => {
      listenerHandle?.remove?.();
    };
  }, []);

  // Use useLayoutEffect to apply theme synchronously before paint
  useLayoutEffect(() => {
    const theme = (getColorTheme() as "light" | "dark") || "dark";
    dispacth(themeReducer({ theme }));

    // Apply to document, body, and #marketview
    document.documentElement.classList.remove("light", "dark");
    document.documentElement.classList.add(theme);
    document.body.classList.remove("light", "dark");
    document.body.classList.add(theme);
    const marketviewElement = document.getElementById("marketview");
    if (marketviewElement) {
      marketviewElement.classList.remove("light", "dark");
      marketviewElement.classList.add(theme);
    }
  }, []);

  // Keep document/body in sync when theme changes
  useEffect(() => {
    if (currentTheme) {
      document.documentElement.classList.remove("light", "dark");
      document.documentElement.classList.add(currentTheme);
      document.body.classList.remove("light", "dark");
      document.body.classList.add(currentTheme);
      const marketviewElement = document.getElementById("marketview");
      if (marketviewElement) {
        marketviewElement.classList.remove("light", "dark");
        marketviewElement.classList.add(currentTheme);
      }
    }
  }, [currentTheme]);

  // ── Global keyboard shortcuts ──────────────────────────────────
  useKeyboardShortcuts();

  return (
    <ErrorBoundary>
      <div>
        {/* Toast notifications */}
        <ToastContainer />
        {/* Desktop-only modals: auto-update, exit confirmation, progress bar */}
        {isDesktop && (
          <>
            <AutoUpdateModal />
            <AppExitModal />
            <ProgressBarModal />
          </>
        )}
        {/* Mobile update modal: Android APK / iOS App Store prompt */}
        <MobileUpdateModal />
        <Router>
          <div className={`App ${currentTheme}`}>
            <Routes>
              <Route element={<WithoutNav />}>
                <Route path="/" element={<Login />} />
              </Route>
              <Route element={<WithNav />}>
                <Route element={<PrivateRoutes />}>
                  <Route
                    path="/Chart"
                    element={
                      <div>
                        <Chart />
                      </div>
                    }
                  />
                  {/* Redirect old routes to Chart */}
                  <Route path="/Dashboard" element={<Navigate to="/Chart" replace />} />
                  <Route path="/MarketWatch" element={<Navigate to="/Chart" replace />} />
                  <Route path="/ChangePassword" element={<Navigate to="/Chart" replace />} />
                </Route>
              </Route>
            </Routes>
          </div>
        </Router>
      </div>
    </ErrorBoundary>
  );
};

export default App;
